import rtti from '@/sui/tools/rtti'
import assert from '@/sui/tools/assert'
import clone from 'clone'

const checkType = {
  bool: {
    f: rtti.isBoolean,
    msg: 'expected boolean.'
  },

  str: {
    f: rtti.isString,
    msg: 'expected string.'
  },

  int: {
    f: rtti.isInteger,
    msg: 'expected integer.'
  },

  num: {
    f: rtti.isNumber,
    msg: 'expected number.'
  },

  pint: {
    f: (f) => rtti.isInteger(f) && f >= 0,
    msg: 'expected positive integer.'
  },

  color: {
    f: (f) => rtti.isString(f) && /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(f),
    msg: 'expected hexadecimal color.'
  },

  percent: {
    f: (f) => rtti.isNumber(f) && f >= 0 && f <= 1,
    msg: 'expected number between 0 and 1.'
  },

  func: {
    f: (f) => rtti.isFunction(f),
    msg: 'expected function.'
  },

  array: {
    f: (f, o, s) => rtti.isArray(f),
    msg: 'expected array.',
    sub: true
  }
}

function functionToString (func) {
  assert.isFunction(func)
  const result = func.toString().replace(/WEBPACK_IMPORTED_MODULE_[0-9]*/g, '').replace(/ +/g, ' ')
  return result
}

export default function (gctx) {
  return {
    errors: [],
    ctx: '',

    check: function (t, m) {
      if (!t) {
        const str = this.ctx.length > 0 ? `${this.ctx}: ${m}` : m
        if (str == null) {
          throw new Error('strange')
        }
        this.errors.push(str)
      }
    },

    checkSchemas: function (ctx, list, sch) {
      if (rtti.isArray(list)) {
        for (let i = 0; i !== list.length; ++i) {
          const v = list[i]
          this.checkSchema(`${ctx}[${i}]`, v, sch)
        }
      } else if (rtti.isObject(list)) {
        for (let k in list) {
          const v = list[k]
          this.checkSchema(`${ctx}[${k}]`, v, sch)
        }
      } else {
        this.errors.push(`${ctx}: invalid list parameter.`)
      }
    },

    checkSchema: function (ctx, obj, sch) {
      let lsch = sch
      if (rtti.isObject(sch)) {
        if (sch.key != null) {
          this.errors.push(`${ctx}: value schema should not include key`)
          return
        }
        lsch = [sch]
      }

      for (let i = 0; i !== lsch.length; ++i) {
        const s = lsch[i]
        const f = s.key == null ? obj : obj[s.key]
        const prefix = `${ctx}${s.key == null ? '' : `[${s.key}]`}`

        if (f == null) {
          if (s.req) {
            this.errors.push(`${prefix}: missing required field ${s.key}`)
          }
          continue
        }

        if (s.check != null) {
          this.check(s.check.f(f, obj), `${prefix} (${f}): ${s.check.msg}`)
        }

        if (s.type != null) {
          const fc = checkType[s.type]
          if (fc == null) {
            this.errors.push(`${prefix} (${f}): invalid schema type ${s.type}.`)
          } else {
            this.check(fc.f(f, obj), `${prefix} (${f}): ${fc.msg}`)
            if (fc.sub && s.sub != null) {
              this.checkSchemas(
                `${prefix}`,
                f,
                s.sub
              )
            }
          }
        }
      }
    },

    checkObjects: function (o, p, st) {
      const keysO = Object.keys(o).sort()
      const stack = st == null ? 'root' : st
      let result = true
      for (let i = 0; i < keysO.length; ++i) {
        const key = keysO[i]
        const vo = o[key]
        const vp = p[key]
        if (vo instanceof Array) {
          if (!(vp instanceof Array)) {
            this.errors.push(`${stack}>${key}: not an Array`)
            result = false
            continue
          }

          if (vo.length !== vp.length) {
            this.errors.push(`${stack}>${key}: different sizes`)
            result = false
            continue
          }

          const vos = clone(vo).sort()
          const vps = clone(vp).sort()

          for (let j = 0; j !== vos.length; ++j) {
            const vosi = vos[j]
            const vpsi = vps[j]
            if (!this.checkObjects(vosi, vpsi, `${stack}>${key}>${j}`)) {
              result = false
            }
          }
        } else if (vo instanceof Date) {
          if (!(vp instanceof Date)) {
            this.errors.push(`${stack}>${key}: not a Date`)
            result = false
            continue
          }
          if (('' + vo) !== ('' + vp)) {
            this.errors.push(`${stack}>${key}: different string values`)
            result = false
            continue
          }
        } else if (vo instanceof rtti.FunctionList) {
          if (!(vp instanceof rtti.FunctionList)) {
            this.errors.push(`${stack}>${key}: not a IgnList`)
            result = false
            continue
          }

          const vol = vo._list
          const vpl = vp._list
          if ((vol == null) !== (vpl == null)) {
            this.errors.push(`${stack}>${key}: different ignore list`)
            result = false
            continue
          }

          if (vol.length !== vpl.length) {
            this.errors.push(`${stack}>${key}: different sizes`)
            result = false
            continue
          }

          for (let j = 0; j !== vol.length; ++j) {
            const voli = vol[j]
            const vpli = vpl[j]
            if (voli !== vpli) {
              if (voli == null || vpli == null) {
                const voliStr = voli == null ? voli : functionToString(voli)
                const vpliStr = vpli == null ? vpli : functionToString(vpli)
                this.errors.push(`${stack}>${key}>${j}/${vol.length - 1}: different functions "${voliStr}" "${vpliStr}}"`)
                result = false
              } else {
                if (voli.name !== vpli.name) {
                  this.errors.push(`${stack}>${key}>${j}/${vol.length - 1}: different function names "${voli.name}" "${vpli.name}}"`)
                  result = false
                }

                const voliStr = functionToString(voli)
                const vpliStr = functionToString(vpli)

                if (voliStr !== vpliStr) {
                  this.errors.push(
                    `${stack}>${key}>${j}/${vol.length - 1}: different function body "${voliStr}" "${vpliStr}"`
                  )
                  result = false
                }
              }
            }
          }
        } else if (vo instanceof Function || vp instanceof Function) {
          if (vo !== vp) {
            if (vo == null || vp == null) {
              const voStr = vo == null ? vo : functionToString(vo)
              const vpStr = vp == null ? vp : functionToString(vp)

              this.errors.push(`${stack}>${key}: different functions "${voStr}" "${vpStr}}"`)
              result = false
            } else {
              if (vo.name !== vp.name) {
                this.errors.push(`${stack}>${key}: different function names "${vo.name}" "${vp.name}}"`)
                result = false
              }

              const voStr = functionToString(vo)
              const vpStr = functionToString(vp)

              if (voStr !== vpStr) {
                this.errors.push(
                  `${stack}>${key}: different function body "${voStr}" "${vpStr}"`
                )
                result = false
              }
            }
          }
        } else if (vo instanceof Object) {
          if (!(vp instanceof Object)) {
            this.errors.push(`${stack}>${key}: not an Object`)
            result = false
            continue
          }
          if (vo === o) { // self reference?
            if (vp !== p) {
              this.errors.push(`${stack}>${key}: not a self reference`)
              result = false
              continue
            }
          } else if (this.checkObjects(vo, vp, `${stack}>${key}`) === false) {
            result = false
            continue // WARNING: does not deal with circular refs other than ^^
          }
        } else if (vo !== vp && (vo != null || vp != null)) { // change !== to != for loose comparison
          this.errors.push(`${stack}>${key}: different values (${vo} / ${vp})`)
          result = false
          continue
        }
      }
      return result
    },

    end: function () {
      if (this.errors.length > 0) {
        const errors = this.errors.join('\n')
        const msg = `\n${gctx}\n${errors}`
        // console.error(msg)
        throw new Error(msg)
      }
    }
  }
}
